import findLastIndex from 'lodash.findlastindex';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { useLoaderData, useSearchParams } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import type { loader as ourProductsLoader } from '@modules/professional/page/loaders/our-products-page-loader.server';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { PROFESSIONAL_TAB_PRODUCT_FILTERS_CONFIG } from '@modules/professional/config/professional-tab-filter-configs';
import { ProfessionalTabFilterSidebar } from './ProfessionalTabFilterSidebar';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import type { FilterStatesKey } from '~/modules/search/type/filter-state';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useProfessionalTabViewMore } from '@modules/professional/hook/use-professional-tab-view-more';
import { useProfessionalHybridLoad } from '@modules/professional/hook/use-professional-hybrid-load';
import { useItemAdminData } from '~/modules/directory/hook/use-item-admin-data';
import {
    DetailTileCard,
    detailCta,
    type DetailTile,
} from '~/ui-rocco/pages/detail-shared';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import MoreProductsComing from '~/modules/products-category/component/DetailedProductSpliter';
import { ProfessionalHeadingCtas } from './ProfessionalHeadingCtas';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';

const PRODUCTS_DESCRIPTION =
    'Browse the products we design and supply. Explore our full range to see the materials, finishes and details that go into every space.';

const IGNORE_FILTER_KEYS: FilterStatesKey[] = [
    ...IN_PAGE_SEARCH_IGNORE_FILTER_KEYS,
    'searchLocation',
];

/**
 * Revamped body for the Products tab (/our-products) — the light-theme sibling
 * of {@link import('./ProfessionalProjectsGridSection')}: a big "Products"
 * title + intro, an "(All Products)" divider, the collapsible left filters
 * (starting collapsed behind a toggle, default light theme) and a 4-column
 * editorial grid of product tiles with infinite scroll. Product tiles reuse the
 * shared {@link DetailTileCard} (Save overlay + BM admin menu) with the
 * clear-cut product treatment on a light panel, per the Figma.
 */
export const ProfessionalProductsGridSection = () => {
    const { productTilesRocco, pagination, filterStates, filterStatesRocco } =
        useLoaderData<typeof ourProductsLoader>();
    const { URLSegment, Title } = useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();

    // Persisted in the site-wide `showFilter` cookie so the show/hide choice
    // survives navigation and stays consistent with the browse pages.
    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    const {
        tiles: loadedTiles,
        hasMore,
        isLoading,
        loadMore,
    } = useProfessionalTabViewMore({
        initialTiles:
            productTilesRocco as TileViewModel<ProductTileViewModel>[],
        pagination,
        urlSegment: URLSegment,
        searchType: 'product',
    });

    const [searchParams] = useSearchParams();
    const { showViewMore, onViewMore, infiniteEnabled, sentinelRef } =
        useProfessionalHybridLoad({
            hasMore,
            isLoading,
            loadMore,
            resetKey: searchParams.toString(),
        });

    const adminDataMap = useItemAdminData(
        loadedTiles.map((tile) => tile.model.id),
    );
    const professionalLink = `/professional/${URLSegment}`;

    const tiles: DetailTile[] = loadedTiles.map((tile) => ({
        id: tile.model.id,
        link: tile.model.link,
        title: tile.model.title,
        images: tile.model.images,
        subtitle: tile.model.professional?.name,
        professional: {
            id: tile.model.professional?.id,
            name: tile.model.professional?.name,
        },
        pinType: 'ProductPin',
        adminData: adminDataMap[tile.model.id],
        professionalLink,
    }));

    const gridColumns = desktop ? (filterDrawerOpen ? 3 : 4) : 2;

    const productNodes = tiles.map((tile) => (
        <DetailTileCard
            key={tile.id}
            tile={tile}
            theme="light"
            mediaClassName="bg-[#f9f6f6] max-md:aspect-[437/524] md:aspect-[437/524]"
        />
    ));

    // When the Buy online (forSale) filter is active, ES ranks purchasable
    // products first and appends the rest. Once the purchasable ones run out,
    // drop in the "no more products for online purchase" divider ahead of the
    // fallback grid, matching the legacy tab.
    if (filterStates.isPurchasable?.checked) {
        const lastPurchasableIndex = findLastIndex(
            loadedTiles,
            (tile) => tile.model.canPurchase,
        );
        const purchasableCount = loadedTiles.filter(
            (tile) => tile.model.canPurchase,
        ).length;
        if (
            purchasableCount === 0 ||
            (lastPurchasableIndex !== -1 &&
                lastPurchasableIndex !== loadedTiles.length - 1)
        ) {
            productNodes.splice(
                lastPurchasableIndex + 1,
                0,
                <MoreProductsComing
                    key="more-products-coming"
                    professionalTitle={Title}
                    columns={gridColumns}
                />,
            );
        }
    }

    const category = useMatchesHandleData<SearchIndex>('category', 'product');
    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys: IGNORE_FILTER_KEYS,
    });

    return (
        <section className="w-full overflow-x-clip bg-white text-[#181818]">
            {/* Desktop keeps the editorial title + intro + CTA cluster; on mobile
                the persistent social-profile header carries the identity/CTAs and
                the tab goes straight to results. */}
            {desktop && (
                <>
                    <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem] md:pt-[3rem]">
                        <div className="flex md:items-start md:justify-between">
                            <h2 className="m-0 font-sans font-normal text-[#181818] md:text-[12.5rem] md:leading-[0.92] md:tracking-[-0.12rem]">
                                Products
                            </h2>
                            <ProfessionalHeadingCtas />
                        </div>
                        <p className="m-0 font-sans leading-[1.25] font-normal text-[#181818]/70 md:mt-[2.4rem] md:max-w-[60.375rem] md:text-[2.0625rem]">
                            {PRODUCTS_DESCRIPTION}
                        </p>
                    </div>
                    <div className="mx-auto w-full max-w-[120rem] md:px-[3.132rem] md:pt-[7.5rem]">
                        <div className="h-px w-full bg-black/15" />
                        <SectionLabel className="mt-[1.062rem] block md:text-[2rem]">
                            (All Products)
                        </SectionLabel>
                    </div>
                </>
            )}
            {/* Filters + grid. On mobile the tab pill names the page and the
                compact filter bar leads; the persistent header carries identity. */}
            <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-5 max-md:pb-14 md:px-[3.132rem] md:pt-[2.4rem] md:pb-[7.5rem]">
                <CollapsiblePageLayout
                    paddingClassName="px-0"
                    sidePanel={
                        desktop &&
                        filterStatesRocco && (
                            <div className="h-full md:pt-[1.8rem]">
                                <ProfessionalTabFilterSidebar
                                    searchIndex="product"
                                    config={
                                        PROFESSIONAL_TAB_PRODUCT_FILTERS_CONFIG
                                    }
                                    initialFilterStates={filterStatesRocco}
                                />
                            </div>
                        )
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <SearchFilter
                        searchFilters={searchFilters}
                        filterDrawerOpen={filterDrawerOpen}
                        toggleFilterDrawer={toggleFilterDrawer}
                        searchBarVariant="revamp"
                        searchPlaceholder="Search products"
                    />
                    {/* The applied-filter bar is desktop-only; on mobile the
                        Refine button's badge + sheet already surface these. */}
                    {desktop &&
                        searchFilters.appliedFilters &&
                        searchFilters.appliedFilters.length > 0 && (
                            <AppliedFilters
                                filterItems={searchFilters.appliedFilters}
                                resetFilters={() =>
                                    searchFilters.resetFilters(
                                        resetFilterStates(
                                            searchFilters.filterStates,
                                            [...IGNORE_FILTER_KEYS, 'category'],
                                        ),
                                    )
                                }
                            />
                        )}

                    {tiles.length === 0 ? (
                        <NoSearchResults resultsType={'products'} />
                    ) : (
                        <>
                            <div
                                className={cn(
                                    'grid grid-cols-2 gap-x-2 gap-y-8 pt-[2.4rem] md:gap-x-[1.5rem] md:gap-y-[3rem]',
                                    // Reduce to 3 columns when the filter
                                    // sidebar is open so tiles keep their size
                                    // rather than shrinking (Figma 2354:59512).
                                    filterDrawerOpen
                                        ? 'md:grid-cols-3'
                                        : 'md:grid-cols-4',
                                )}
                            >
                                {productNodes}
                            </div>
                            {infiniteEnabled && hasMore && (
                                <div
                                    ref={sentinelRef}
                                    aria-hidden="true"
                                    style={{ height: 1 }}
                                />
                            )}
                            {showViewMore && (
                                <div className="flex justify-center pt-[3rem]">
                                    <button
                                        type="button"
                                        onClick={onViewMore}
                                        className={detailCta(
                                            'outlined-light',
                                            'max-md:h-[3.125rem] max-md:text-sm',
                                        )}
                                    >
                                        View more
                                    </button>
                                </div>
                            )}
                            {isLoading && (
                                <div className="flex justify-center pt-[3rem]">
                                    <Spinner className="size-8 text-black" />
                                </div>
                            )}
                        </>
                    )}
                </CollapsiblePageLayout>
            </div>
        </section>
    );
};
